import { TemplateFolderModule } from "@/types/forms/form-template-folder.types.ts";
import i18n from "@/i18n.ts";
import {
  ApiFormEdit,
  ApiFormElementEdit,
  ApiFormElementSelectOptionEdit,
  ApiFormElementWriteToFieldTableMode,
  ApiFormElementWriteToFieldTargetType,
} from "@/types/forms/form-edit.types.ts";
import { ApiForm } from "@/types/forms/form-view.types.ts";
import { ApiFormElementType, ApiFormType } from "@/types/forms/form.types.ts";
import { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";
import { ApiOperationResult } from "@/types/common.types.ts";
import { ApiFormTemplate, ApiFormTemplateSavePayload } from "@/types/forms/form-template.types.ts";
import { ApiFieldTypes } from "@/types/custom-field.types.ts";

export const selectScaleOptionSchema = z.object({
  id: z.number().optional(),
  sort: z.number().optional(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  emoji: z.string().nullable(),
  value: z
    .union([z.number(), z.string()])
    .nullable()
    .transform((value) =>
      value === null || value === "" || isNaN(Number(value)) ? null : Number(value)
    ),
});

export const elementScaleSettingsSchema = z.object({
  title: z
    .string({ error: i18n.t("errors:field_required") })
    .min(1, i18n.t("errors:field_required")),
  description: z.string().nullable(),
  is_required: z.boolean({ error: i18n.t("errors:field_required") }),
  is_comment_enabled: z.boolean({ error: i18n.t("errors:field_required") }),
  form_scale_id: z.number({ error: i18n.t("errors:field_required") }),
});

export const elementGoalsSettingsSchema = z
  .object({
    title: z
      .string({ error: i18n.t("errors:field_required") })
      .min(1, i18n.t("errors:field_required")),
    description: z.string().nullable(),
    is_required: z.boolean({ error: i18n.t("errors:field_required") }),
    is_comment_enabled: z.boolean({ error: i18n.t("errors:field_required") }),
    form_scale_id: z.number({ error: i18n.t("errors:field_required") }),
    date_starts_at: z.date({ error: i18n.t("errors:field_required") }),
    date_ends_at: z.date({ error: i18n.t("errors:field_required") }),
    is_single_rating: z.boolean().optional(),
    show_when_no_goals: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.date_ends_at < data.date_starts_at) {
      ctx.addIssue({
        code: "custom",
        message: i18n.t("errors:end_date_should_be_greater_start_date"),
        path: ["date_ends_at"],
      });
    }
  });

export const elementSelectSettingsSchema = z.object({
  title: z
    .string({ error: i18n.t("errors:field_required") })
    .min(1, i18n.t("errors:field_required")),
  description: z.string().nullable(),
  is_required: z.boolean({ error: i18n.t("errors:field_required") }),
  is_comment_enabled: z.boolean({ error: i18n.t("errors:field_required") }),
  is_multiple: z.boolean({ error: i18n.t("errors:field_required") }),
  is_other: z.boolean({ error: i18n.t("errors:field_required") }),
  options: z.array(selectScaleOptionSchema).min(1, i18n.t("errors:field_required")),
});

export const elementOpenAnswerSettingsSchema = z.object({
  title: z
    .string({ error: i18n.t("errors:field_required") })
    .min(1, i18n.t("errors:field_required")),
  description: z.string().nullable(),
  is_required: z.boolean({ error: i18n.t("errors:field_required") }),
  is_comment_enabled: z.boolean({ error: i18n.t("errors:field_required") }),
});

export const elementFileSettingsSchema = z.object({
  title: z
    .string({ error: i18n.t("errors:field_required") })
    .min(1, i18n.t("errors:field_required")),
  description: z.string().nullable(),
  is_required: z.boolean({ error: i18n.t("errors:field_required") }),
  is_comment_enabled: z.boolean({ error: i18n.t("errors:field_required") }),
});

export const elementEmployeeNPSSettingsSchema = z.object({
  title: z
    .string({ error: i18n.t("errors:field_required") })
    .min(1, i18n.t("errors:field_required")),
  description: z.string().nullable(),
  is_required: z.boolean(),
  is_comment_enabled: z.boolean(),
});

export const elementWriteToFieldSettingsSchema = z
  .object({
    title: z
      .string({ error: i18n.t("errors:field_required") })
      .min(1, i18n.t("errors:field_required")),
    description: z.string().nullable(),
    is_required: z.boolean(),
    is_comment_enabled: z.boolean(),
    target_type: z.enum(ApiFormElementWriteToFieldTargetType, {
      error: i18n.t("errors:field_required"),
    }),
    field_id: z.number().nullable(),
    field_type: z.enum(ApiFieldTypes).nullable(),
    table_id: z.number().nullable(),
    table_mode: z.enum(ApiFormElementWriteToFieldTableMode).nullable().optional(),
    show_current_row: z.boolean().optional(),
    fill_in_all_columns: z.boolean().optional(),
    column_ids_to_fill: z.array(z.number()).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.target_type === ApiFormElementWriteToFieldTargetType.field) {
      if (data.field_id == null) {
        ctx.addIssue({
          code: "custom",
          message: i18n.t("errors:field_required"),
          path: ["field_id"],
        });
      }
      if (data.field_type == null) {
        ctx.addIssue({
          code: "custom",
          message: i18n.t("errors:field_required"),
          path: ["field_type"],
        });
      }
    }
    if (data.target_type === ApiFormElementWriteToFieldTargetType.table && data.table_id == null) {
      ctx.addIssue({
        code: "custom",
        message: i18n.t("errors:field_required"),
        path: ["table_id"],
      });
    }
  });

export const elementTextSettingsSchema = z.object({
  text: z.string().nullable(),
  title: z
    .string({ error: i18n.t("errors:field_required") })
    .min(1, i18n.t("errors:field_required")),
  description: z.string().nullable(),
});

export const formElementSchema = z
  .object({
    id: z.number().optional(),
    type: z.enum(ApiFormElementType),
    scale_settings: elementScaleSettingsSchema.optional(),
    select_settings: elementSelectSettingsSchema.optional(),
    open_answer_settings: elementOpenAnswerSettingsSchema.optional(),
    file_settings: elementFileSettingsSchema.optional(),
    employee_nps_settings: elementEmployeeNPSSettingsSchema.optional(),
    goals_settings: elementGoalsSettingsSchema.optional(),
    text_settings: elementTextSettingsSchema.optional(),
    write_to_field_settings: elementWriteToFieldSettingsSchema.optional(),
  })
  .superRefine((value, ctx) => {
    const {
      type,
      scale_settings,
      select_settings,
      open_answer_settings,
      file_settings,
      employee_nps_settings,
      goals_settings,
      text_settings,
      write_to_field_settings,
    } = value;

    let hasSettings: boolean;
    switch (type) {
      case ApiFormElementType.scale:
        hasSettings = !!scale_settings;
        break;
      case ApiFormElementType.select:
        hasSettings = !!select_settings;
        break;
      case ApiFormElementType.open_answer:
        hasSettings = !!open_answer_settings;
        break;
      case ApiFormElementType.file:
        hasSettings = !!file_settings;
        break;
      case ApiFormElementType.employee_nps:
        hasSettings = !!employee_nps_settings;
        break;
      case ApiFormElementType.goals:
        hasSettings = !!goals_settings;
        break;
      case ApiFormElementType.text:
        hasSettings = !!text_settings;
        break;
      case ApiFormElementType.write_to_field:
        hasSettings = !!write_to_field_settings;
        break;
      default:
        hasSettings = false;
    }

    if (!hasSettings) {
      ctx.addIssue({
        code: "custom",
        message: "At least one settings field must be provided based on element type",
      });
    }
  }) as unknown as z.ZodType<ApiFormElementEdit>;

const formSectionSchema = z.object({
  id: z.number().optional(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  elements: z.array(formElementSchema),
});

const formPageSchema = z.object({
  id: z.number().optional(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  sections: z.array(formSectionSchema),
});

export const apiFormEditSchema = z.object({
  pages: z.array(formPageSchema).min(1),
});

export const createDefaultElement = (type: ApiFormElementType) => {
  switch (type) {
    case ApiFormElementType.text:
      return {
        type: type,
        text_settings: {},
      } as ApiFormElementEdit;
    case ApiFormElementType.open_answer:
      return {
        type: type,
        open_answer_settings: {
          is_required: true,
        },
      } as ApiFormElementEdit;
    case ApiFormElementType.file:
      return {
        type,
        file_settings: {
          title: null,
          description: null,
          is_required: false,
          is_comment_enabled: false,
        },
      } as ApiFormElementEdit;
    case ApiFormElementType.employee_nps:
      return {
        type: type,
        employee_nps_settings: {
          is_required: true,
          title: i18n.t("surveys:employee_nps_element_title_default"),
          description: i18n.t("surveys:employee_nps_element_description_default"),
        },
      } as ApiFormElementEdit;
    case ApiFormElementType.select:
      return {
        type: type,
        select_settings: {
          is_required: true,
          options: [] as ApiFormElementSelectOptionEdit[],
        },
      } as ApiFormElementEdit;
    case ApiFormElementType.write_to_field:
      return {
        type: type,
        write_to_field_settings: {
          title: null,
          description: null,
          is_required: true,
          is_comment_enabled: false,
          target_type: ApiFormElementWriteToFieldTargetType.field,
          field_id: null,
          field_type: null,
          table_id: null,
          table_mode: null,
          show_current_row: true,
          fill_in_all_columns: null,
          column_ids_to_fill: null,
        },
      } as ApiFormElementEdit;
    default:
      throw new Error(`Unsupported element type: ${type}`);
  }
};

export interface FormProviderTypeUpdateParams {
  id: number;
  payload: ApiFormEdit;
}

export interface FormProviderTypeReset {
  id: number;
}

export interface FormProviderTypeSaveAsTemplate {
  id: number;
  payload: ApiFormTemplateSavePayload;
}

export interface FormProviderType {
  type: ApiFormType;
  templateFolderModule?: TemplateFolderModule;
  hooks: {
    useGetFormById: (id: number) => UseQueryResult<ApiForm>;
    useUpdateForm: () => UseMutationResult<void, Error, FormProviderTypeUpdateParams, unknown>;
    useResetForm: () =>
      UseMutationResult<ApiOperationResult, Error, FormProviderTypeReset, unknown> | undefined;
    useSaveAsTemplate: () =>
      | UseMutationResult<ApiFormTemplate, Error, FormProviderTypeSaveAsTemplate, unknown>
      | undefined;
  };
}
